import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileSignature, Plus, Loader2, Trash2, FileText, ScrollText, HandCoins, BriefcaseBusiness, ChevronRight } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const DOC_ICONS = {
  company_constitution: ScrollText,
  sha: FileText,
  safe: HandCoins,
  employment_agreement: BriefcaseBusiness,
};

export default function DocumentBuilder() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [catalog, setCatalog] = useState({ templates: [], profiles: [], stances: {} });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ doc_type: "", title: "", recipient_profile: "" });

  const load = async () => {
    if (!company) return;
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get(`/companies/${company.id}/builder-documents`),
        api.get(`/document-builder/catalog`),
      ]);
      setDocs(d.data);
      setCatalog(c.data);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const startCreate = () => { setForm({ doc_type: "", title: "", recipient_profile: "" }); setOpen(true); };

  const create = async () => {
    if (!form.doc_type) return toast.error("Choose a document type");
    setCreating(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/builder-documents`, form);
      toast.success("Document created");
      setOpen(false);
      navigate(`/app/document-builder/${data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create"); }
    finally { setCreating(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/builder-documents/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const selectedTemplate = catalog.templates.find((t) => t.doc_type === form.doc_type);

  return (
    <AppShell
      title="Document Builder"
      subtitle="Configure investor & team documents by stance, then export or share"
      actions={<Button className="gap-2" onClick={startCreate} data-testid="new-document-btn"><Plus className="h-4 w-4" /> New document</Button>}
    >
      <div className="mb-6 flex items-start gap-2 rounded-lg border border-dashed bg-secondary/40 px-3 py-2 text-xs text-muted-foreground" data-testid="legal-disclaimer">
        <FileSignature className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        <span>These are configurable starting points, <strong>not legal advice</strong>. Have a qualified lawyer review before signing.</span>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : docs.length === 0 ? (
        <EmptyState icon={FileSignature} title="No documents yet" description="Create your first document from a template and tune each clause to the right stance." actionLabel="New document" onAction={startCreate} />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {docs.map((d) => {
            const Icon = DOC_ICONS[d.doc_type] || FileText;
            return (
              <Card key={d.id} className="group flex items-center justify-between rounded-xl border bg-card p-4 transition-colors hover:border-primary/40" data-testid={`builder-doc-${d.id}`}>
                <button className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => navigate(`/app/document-builder/${d.id}`)}>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-foreground">{d.title}</div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{d.template_name}</span>
                      <span>·</span>
                      <span>v{d.current_version}</span>
                      {d.recipient_profile && <Badge variant="secondary" className="h-4 px-1.5 text-[10px] font-normal capitalize">{d.recipient_profile}</Badge>}
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">Updated {formatDateTime(d.updated_at)}</div>
                  </div>
                </button>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => remove(d.id)} data-testid={`builder-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="create-document-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-primary" /> New document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Document type</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm((f) => ({ ...f, doc_type: v, title: catalog.templates.find((t) => t.doc_type === v)?.name || "" }))}>
                <SelectTrigger className="mt-1" data-testid="doc-type-select"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
                <SelectContent>
                  {catalog.templates.map((t) => <SelectItem key={t.doc_type} value={t.doc_type} data-testid={`doc-type-${t.doc_type}`}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedTemplate && <p className="mt-1 text-xs text-muted-foreground">{selectedTemplate.description} · {selectedTemplate.clause_count} clauses</p>}
            </div>
            <div>
              <Label>Recipient profile (sets the overall stance)</Label>
              <Select value={form.recipient_profile} onValueChange={(v) => setForm((f) => ({ ...f, recipient_profile: v }))}>
                <SelectTrigger className="mt-1" data-testid="recipient-profile-select"><SelectValue placeholder="Optional - e.g. Angel, VC, Employee" /></SelectTrigger>
                <SelectContent>
                  {catalog.profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`profile-${p.id}`}>
                      {p.name} - {catalog.stances[p.default_stance]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Seed SAFE - Acme Ventures" className="mt-1" data-testid="doc-title-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={creating || !form.doc_type} className="gap-2" data-testid="create-document-submit">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create & edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
